/**
 * Calendar Assistant Knowledge Base
 * 
 * Comprehensive knowledge about the Calendar page
 * for the Calendar Assistant to help users with scheduling and calendar management.
 */

export const CALENDAR_SECTIONS = {
  overview: {
    name: 'Calendar Overview',
    icon: '📅',
    description: 'The Calendar page is your central hub for managing appointments, meetings, and scheduling.',
    purpose: 'Here you can view, create, and manage events, sync with Google Calendar, and configure calendar settings.',
    layout: {
      topBar: 'Contains timezone display, live time, sync button, and settings button',
      toolbar: 'View controls (Day, Week, Month, Year, Agenda), timeline toggle, and options',
      mainArea: 'The calendar grid showing your events',
      settingsPanel: 'Right panel with calendar configuration options (hidden by default)'
    }
  },

  views: {
    name: 'Calendar Views',
    icon: '👁️',
    description: 'Different ways to visualize your schedule.',
    options: {
      day: {
        name: 'Day View',
        description: 'Shows a single day with hourly time slots',
        icon: 'e-day',
        useCase: 'Best for detailed daily planning'
      },
      week: {
        name: 'Week View',
        description: 'Shows 7 days with time slots',
        icon: 'e-week',
        useCase: 'Default view for weekly overview'
      },
      workWeek: {
        name: 'Work Week View',
        description: 'Shows only your work days (configured in settings)',
        icon: 'e-week',
        useCase: 'Focus on business days only'
      },
      month: {
        name: 'Month View',
        description: 'Shows entire month with events as compact blocks',
        icon: 'e-month',
        useCase: 'Best for long-term planning'
      },
      year: {
        name: 'Year View',
        description: 'Shows 12 months at a glance',
        icon: 'e-month',
        useCase: 'Annual overview and planning'
      },
      agenda: {
        name: 'Agenda View',
        description: 'List of upcoming events in chronological order',
        icon: 'e-agenda-date-range',
        useCase: 'Quick scan of upcoming appointments'
      }
    },
    timelineToggle: {
      name: 'Timeline Views',
      description: 'Checkbox to switch between standard and timeline layout',
      effect: 'When enabled, shows Day, Week, Month in horizontal timeline format'
    }
  },

  toolbar: {
    name: 'Toolbar Controls',
    icon: '🔧',
    description: 'Top toolbar with view controls and options.',
    buttons: {
      newEvent: {
        name: 'New Event',
        icon: 'e-plus',
        description: 'Create a new single event',
        howTo: 'Click to open event creation dialog'
      },
      newRecurringEvent: {
        name: 'New Recurring Event',
        icon: 'e-repeat',
        description: 'Create a repeating event',
        howTo: 'Click to create event with recurrence options'
      }
    },
    toggles: {
      timelineViews: 'Switch between grid and timeline layout',
      grouping: 'Group events by calendar/resource',
      gridlines: 'Show/hide time slot gridlines',
      rowAutoHeight: 'Automatically adjust row height based on content'
    }
  },

  appBar: {
    name: 'App Bar',
    icon: '📊',
    description: 'Top bar with timezone and sync controls.',
    elements: {
      timezone: {
        name: 'Timezone Display',
        description: 'Shows current calendar timezone',
        icon: 'e-time-zone'
      },
      liveTime: {
        name: 'Live Time',
        description: 'Shows current time updating in real-time',
        icon: 'e-clock'
      },
      syncButton: {
        name: 'Sync to Google',
        description: 'Syncs events with Google Calendar',
        visibility: 'Only visible when Google Calendar is connected',
        howTo: 'Click to sync iMaven events to Google Calendar'
      },
      settingsButton: {
        name: 'Settings',
        description: 'Opens the right settings panel',
        icon: 'e-settings'
      }
    }
  },

  googleCalendarSync: {
    name: 'Google Calendar Sync',
    icon: '🔄',
    description: 'Integration with Google Calendar.',
    features: {
      bidirectionalSync: 'Events sync both ways between iMaven and Google Calendar',
      autoMerge: 'Google events appear in your iMaven calendar',
      syncButton: 'Manual sync button appears when connected'
    },
    howToConnect: 'Go to Profile → Calendar Integration → Connect Google Calendar',
    syncMessages: {
      success: 'Shows count of synced and cleaned up events',
      inSync: 'Shows "Already in sync!" when no changes needed',
      error: 'Shows error message if sync fails'
    }
  },

  eventManagement: {
    name: 'Event Management',
    icon: '📝',
    description: 'Creating, editing, and deleting events.',
    createEvent: {
      methods: [
        'Click "New Event" button in toolbar',
        'Double-click on an empty time slot',
        'Right-click on empty slot → Add Event',
        'Drag to select time range'
      ],
      fields: {
        subject: 'Event title/name',
        startTime: 'When the event starts',
        endTime: 'When the event ends',
        location: 'Where the event takes place',
        description: 'Details about the event',
        isAllDay: 'Toggle for all-day events',
        calendar: 'Which calendar to add to'
      }
    },
    editEvent: {
      howTo: 'Click on an event to open quick info, then click Edit',
      note: 'Single (non-recurring) events block editing by default in editor popup'
    },
    deleteEvent: {
      methods: [
        'Right-click on event → Delete',
        'For recurring: choose Delete Occurrence or Delete Series'
      ]
    },
    recurringEvents: {
      description: 'Events that repeat on a schedule',
      options: [
        'Daily',
        'Weekly',
        'Monthly',
        'Yearly',
        'Custom pattern'
      ],
      contextMenu: 'Right-click recurring events for Edit/Delete options'
    }
  },

  settingsPanel: {
    name: 'Settings Panel',
    icon: '⚙️',
    description: 'Right panel with calendar configuration.',
    howToOpen: 'Click the settings (gear) icon in the top right',
    sections: {
      calendar: {
        name: 'Calendar Selection',
        description: 'Choose which calendars to display',
        howTo: 'Check/uncheck calendars to show or hide them'
      },
      firstDayOfWeek: {
        name: 'First Day of Week',
        description: 'Which day starts your week',
        options: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        howTo: 'Select from dropdown'
      },
      workWeek: {
        name: 'Work Week',
        description: 'Which days are your work days',
        howTo: 'Check the days you work'
      },
      timezone: {
        name: 'Timezone',
        description: 'Your calendar timezone',
        howTo: 'Select from dropdown list'
      },
      dayHours: {
        name: 'Day Start/End Hour',
        description: 'When your calendar day starts and ends',
        fields: ['Day Start Hour', 'Day End Hour'],
        howTo: 'Use time pickers to set hours'
      },
      workHours: {
        name: 'Work Hours',
        description: 'Your working hours (highlighted in calendar)',
        fields: ['Work Start Hour', 'Work End Hour'],
        howTo: 'Use time pickers to set work hours'
      },
      slotDuration: {
        name: 'Slot Duration',
        description: 'Length of each time slot in minutes',
        options: [15, 30, 45, 60],
        howTo: 'Select from dropdown'
      },
      slotInterval: {
        name: 'Slot Interval',
        description: 'Number of slots per major interval',
        howTo: 'Select from dropdown'
      },
      timeFormat: {
        name: 'Time Format',
        description: '12-hour or 24-hour time display',
        options: ['12h (AM/PM)', '24h'],
        howTo: 'Select from dropdown'
      },
      weekNumbers: {
        name: 'Week Numbers',
        description: 'Show week numbers on calendar',
        options: ['Off', 'FirstDay', 'FirstFourDayWeek', 'FirstFullWeek'],
        howTo: 'Select from dropdown'
      },
      tooltip: {
        name: 'Tooltip',
        description: 'Show event details on hover',
        options: ['On', 'Off'],
        howTo: 'Toggle on or off'
      }
    }
  },

  contextMenu: {
    name: 'Context Menu',
    icon: '📋',
    description: 'Right-click menu for quick actions.',
    onEmptySlot: ['Add Event', 'Add Recurring Event', 'Today'],
    onEvent: ['Delete (for recurring)', 'Delete Occurrence', 'Delete Series']
  },

  importExport: {
    name: 'Import/Export',
    icon: '📤',
    description: 'Import and export calendar data.',
    features: {
      import: 'Import ICS files (iCalendar format)',
      exportExcel: 'Export events to Excel spreadsheet',
      exportICS: 'Export events to ICS file'
    },
    note: 'Import/Export buttons may be hidden in current version'
  },

  print: {
    name: 'Print Calendar',
    icon: '🖨️',
    description: 'Print your calendar view.',
    howTo: 'Click Print button (if available) to open print dialog',
    note: 'Print button may be hidden in current version'
  }
};

export const COMMON_CALENDAR_QUESTIONS = {
  'How do I create a new event?': 'Click the "New Event" button in the toolbar, or double-click on an empty time slot to open the event creation dialog.',
  'How do I view my schedule for the week?': 'Click the "Week" button in the toolbar. For timeline layout, enable the "Timeline Views" checkbox first.',
  'How do I sync with Google Calendar?': 'First connect Google Calendar in Profile → Calendar Integration. Then click the "Sync to Google" button in the top right of the calendar.',
  'How do I change my timezone?': 'Click the settings (gear) icon in the top right, then select your timezone from the dropdown in the settings panel.',
  'How do I delete an event?': 'Right-click on the event and select "Delete". For recurring events, choose whether to delete just this occurrence or the entire series.',
  'How do I create a recurring event?': 'Click "New Recurring Event" in the toolbar, or create a regular event and enable recurrence in the event editor.',
  'How do I change the first day of the week?': 'Open settings panel (gear icon), find "First Day of Week" dropdown, and select your preferred day.',
  'How do I see only my work days?': 'Click the "WorkWeek" view button in the toolbar. Configure which days are work days in the settings panel.',
  'Why can\'t I edit an event?': 'Single (non-recurring) events have editing blocked in the popup. You can still delete and recreate events.',
  'How do I change my work hours?': 'Open settings panel and adjust "Work Start Hour" and "Work End Hour" time pickers.',
  'How do I navigate to today?': 'Right-click on any empty area and select "Today" from the context menu.',
  'How do I change the time slot duration?': 'Open settings panel and select a new value from the "Slot Duration" dropdown.',
  'Where are my Google Calendar events?': 'Google events appear automatically after connecting your calendar in Profile settings. They merge with your iMaven events.'
};

export const CALENDAR_WORKFLOWS = {
  scheduleClientMeeting: {
    name: 'Schedule a Client Meeting',
    steps: [
      '1. Navigate to the Calendar page from the sidebar',
      '2. Find an available time slot in your schedule',
      '3. Click "New Event" or double-click the time slot',
      '4. Enter meeting details (subject, location, description)',
      '5. Set start and end times',
      '6. Click Save to create the event',
      '7. If connected, sync to Google Calendar'
    ]
  },
  setUpWorkSchedule: {
    name: 'Set Up Your Work Schedule',
    steps: [
      '1. Click the settings (gear) icon in the top right',
      '2. Set your "First Day of Week"',
      '3. Check your work days in "Work Week"',
      '4. Set "Work Start Hour" and "Work End Hour"',
      '5. Configure "Day Start Hour" and "Day End Hour"',
      '6. Changes save automatically'
    ]
  },
  syncGoogleCalendar: {
    name: 'Sync with Google Calendar',
    steps: [
      '1. First, go to Profile → Calendar Integration',
      '2. Click "Connect Google Calendar" and authorize',
      '3. Return to Calendar page',
      '4. Click "Sync to Google" button in top bar',
      '5. Events sync both directions'
    ]
  },
  createWeeklyRecurring: {
    name: 'Create a Weekly Recurring Meeting',
    steps: [
      '1. Click "New Recurring Event" in the toolbar',
      '2. Enter event details',
      '3. In recurrence options, select "Weekly"',
      '4. Choose which days to repeat',
      '5. Set end date or number of occurrences',
      '6. Click Save'
    ]
  }
};

/**
 * Get contextual help based on the current calendar action
 */
export const getCalendarContextualHelp = (context) => {
  const suggestions = [];
  
  // General calendar suggestions
  suggestions.push('How do I create a new event?');
  suggestions.push('How do I change my calendar view?');
  suggestions.push('How do I sync with Google Calendar?');
  suggestions.push('How do I configure my work hours?');
  
  return { suggestions };
};

export default {
  CALENDAR_SECTIONS,
  COMMON_CALENDAR_QUESTIONS,
  CALENDAR_WORKFLOWS,
  getCalendarContextualHelp
};

